import { ChevronLeftIcon, ChevronRightIcon } from "@xola/icons";
import { Button } from "@xola/ui-kit";
import React, { useState } from "react";
import { EventObject } from "./useCalendarEvents";
import { DashboardScope } from "@/types/Dashboard";

export interface CalendarResource {
    id: ID;
    title?: string;
}

interface DashboardEquipmentPaginationProps {
    calendarResources: CalendarResource[];
    scope: DashboardScope;
    allTimeslots: EventObject[];
}

export const DashboardEquipmentPagination = ({
    calendarResources,
    scope,
    allTimeslots,
}: DashboardEquipmentPaginationProps) => {
    const {
        currentPage,
        handlePrev,
        handleNext,
        resetPage: resetEquipmentPage,
        visibleResources,
        totalResources,
        totalPages,
    } = useEquipmentPagination(calendarResources, scope, allTimeslots);

    return {
        resetEquipmentPage,
        visibleResources,
        jsx:
            totalResources > 0 ? (
                <div>
                    <Button
                        size="tiny"
                        disabled={currentPage === 0}
                        className="resource-prev-button"
                        onClick={handlePrev}
                    >
                        <ChevronLeftIcon />
                    </Button>
                    <Button
                        size="tiny"
                        disabled={currentPage === totalPages - 1}
                        className="resource-next-button"
                        onClick={handleNext}
                    >
                        <ChevronRightIcon />
                    </Button>
                </div>
            ) : null,
    };
};

const useEquipmentPagination = (resources: CalendarResource[], scope: DashboardScope, eventObjects: EventObject[]) => {
    const calendarResources = resources.sort(compareTitles);

    const isAssignedScope = scope === DashboardScope.ASSIGNED;
    const [currentPage, setCurrentPage] = useState(0);

    const handlePrevious = () => {
        if (currentPage > 0) {
            setCurrentPage((previousPage) => previousPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage((previousPage) => previousPage + 1);
        }
    };

    const resetPage = () => {
        setCurrentPage(0);
    };

    // Filter resources with events based when selected scope is "Assigned"
    const filteredCalendarResources = isAssignedScope
        ? calendarResources.filter((item) => {
              return eventObjects.some(
                  (eventObject: EventObject) =>
                      eventObject.resourceId === item.id || eventObject.resourceIds?.includes(item.id),
              );
          })
        : calendarResources;

    const visibleColumns = 5;
    const totalResources = filteredCalendarResources.length;

    // Calculate the total number of pages based on the number of resources and the number of visible columns
    const totalPages = Math.ceil(totalResources / visibleColumns);

    const startIndex = currentPage * visibleColumns;
    const endIndex = startIndex + visibleColumns;

    const visibleResources = filteredCalendarResources.slice(startIndex, endIndex);

    return {
        currentPage,
        handlePrev: handlePrevious,
        handleNext,
        resetPage,
        visibleResources,
        totalResources,
        totalPages,
    };
};

const compareTitles = (a: CalendarResource, b: CalendarResource) => (a?.title ?? "").localeCompare(b?.title ?? "");
